import { useEffect, useRef, useState } from 'preact/hooks'

interface Shown<T> {
  readonly item: T
  /** Gone from the list, kept for `ms` so it can fade out. */
  readonly leaving: boolean
}

/**
 * `items`, plus the ones that just left, each kept for `ms` at the place it had, so it can
 * animate out. With `ms` 0 (reduced motion) nothing is kept.
 */
export function useLeaving<T extends { readonly id: string }>(
  items: readonly T[],
  ms: number,
): readonly Shown<T>[] {
  const [left, setLeft] = useState<readonly { item: T; index: number }[]>([])
  const prevRef = useRef(items)
  const timersRef = useRef(new Set<ReturnType<typeof setTimeout>>())

  useEffect(() => {
    const ids = new Set(items.map((item) => item.id))
    const gone = prevRef.current.flatMap((item, index) =>
      ids.has(item.id) ? [] : [{ item, index }],
    )
    prevRef.current = items
    if (gone.length === 0 || ms === 0) return
    const goneIds = new Set(gone.map((entry) => entry.item.id))
    setLeft((current) => [...current.filter((entry) => !goneIds.has(entry.item.id)), ...gone])
    const timer = setTimeout(() => {
      timersRef.current.delete(timer)
      setLeft((current) => current.filter((entry) => !gone.includes(entry)))
    }, ms)
    timersRef.current.add(timer)
  }, [items, ms])

  useEffect(() => {
    const pending = timersRef.current
    return () => {
      for (const timer of pending) clearTimeout(timer)
    }
  }, [])

  const ids = new Set(items.map((item) => item.id))
  const shown: Shown<T>[] = items.map((item) => ({ item, leaving: false }))
  for (const { item, index } of left) {
    // One that came back is shown as staying.
    if (!ids.has(item.id)) shown.splice(Math.min(index, shown.length), 0, { item, leaving: true })
  }
  return shown
}
